import { isLocalStorageAvailable, m365Nonce } from "#webapp/src/state/local-storage";
import { logger } from "#webapp/src/util/logger.util";
import { getSiteUrl } from "@clockwise/client-commons/src/config/api";
import { outlookPaths } from "@clockwise/client-commons/src/constants/site";
import { useCallback, useEffect, useRef, useState } from "react";

export const canAuth = () => {
  // Since our OAuth flows require validating a nonce from separate windows, local storage is a requirement
  return isLocalStorageAvailable();
};

/**
 * Auth response message utils
 */

export type AuthResponsePayload = {
  success: boolean;
  message: string;
  authCode?: string;
  error?: string;
};

export const sendMessageToParentWindow = (payload: AuthResponsePayload) => {
  const targetOrigin = new URL(getSiteUrl()).origin;
  const parentWindow: Window | undefined = window.opener;
  if (!parentWindow?.postMessage) {
    return logger.error("failed to find window.opener for auth redirect");
  }

  parentWindow.postMessage(payload, targetOrigin);
};

export const OAUTH_COMPLETE_MESSAGE = "cw-oauth-complete";

const isAuthResponseMessage = (event: MessageEvent): event is MessageEvent<AuthResponsePayload> => {
  const redirectOrigin = new URL(getSiteUrl()).origin;
  return event.origin === redirectOrigin && event.data?.message === OAUTH_COMPLETE_MESSAGE;
};

/**
 * Auth hooks
 */

type OAuthPromptOpts = {
  onSuccess?: (result: { code: string }) => void;
  onFailure?: (result: { error: string }) => void;
};

const useMessageHandler = <E extends MessageEvent>() => {
  const messageHandler = useRef<(e: E) => void>();
  const unlisten = useCallback(() => {
    if (messageHandler.current) {
      window.removeEventListener("message", messageHandler.current);
    }
    messageHandler.current = undefined;
  }, []);
  const listen = useCallback(
    (handler: (e: E) => void) => {
      unlisten();
      window.addEventListener("message", handler);
      messageHandler.current = handler;
    },
    [unlisten],
  );

  // Remove active listeners before unmounting to prevent memory leak
  useEffect(() => unlisten, [unlisten]);
  return { listen, unlisten };
};

/**
 * Sends a message to the Outlook add-in to notify it of the auth response.
 * Important. This function should only be called after Office is defined and we know if we're in Outlook.
 * This happens in outlool.tsx.
 */
export const sendMessageToOutlookAddIn = async (payload: AuthResponsePayload) => {
  try {
    window.Office.context.ui.messageParent(JSON.stringify(payload));
  } catch (err) {
    logger.error("failed to send auth response to office add-in", err);
  }
};

export const makeOAuthHook = (buildOAuthUrl: (scopes: string[]) => string) => () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<{ code: string }>();

  const { listen, unlisten } = useMessageHandler<MessageEvent<AuthResponsePayload>>();

  const prompt = useCallback(
    (scopes: string[], { onSuccess, onFailure }: OAuthPromptOpts = {}) => {
      setLoading(true);

      const oauthUrl = buildOAuthUrl(scopes);

      listen((event) => {
        if (!isAuthResponseMessage(event)) return;

        // We got the correct event, no need to listen anymore
        unlisten();

        const { success, authCode, error } = event.data;

        if (error || !success || !authCode) {
          const errorMessage = error || "Invalid auth response";
          setError(errorMessage);
          onFailure?.({ error: errorMessage });
        } else {
          const response = { code: authCode };
          setData(response);
          setError("");
          onSuccess?.(response);
        }
        setLoading(false);
      });

      const windowFeatures = [
        "scrollbars=yes,resizable=yes",
        "toolbar=no,directories=no,status=no,menubar=no,copyhistory=no",
        "width=600,height=870",
      ].join();
      window.open(oauthUrl, "_blank", windowFeatures);
    },
    [listen, unlisten],
  );

  return {
    prompt,
    loading,
    error,
    data,
  } as const;
};

export const m365NonceUrlParam = "m365Nonce";

export const makeOAuthHookOutlook = (buildOAuthUrl: (scopes: string[]) => string) => () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<{ code: string }>();

  const prompt = useCallback((scopes: string[], { onSuccess, onFailure }: OAuthPromptOpts = {}) => {
    setLoading(true);

    const oauthUrl = buildOAuthUrl(scopes);
    const m365None =
      m365Nonce.get() ||
      "Error, no nonce found. Will cause error attempting auth on Safari and Firefox.";

    // Outlook requires you to open new windows to do auth with using their API.

    // Office displayDialogAsync function requires that the location that you open is the same
    // origin as the add-in. This is why we are using the same origin as the add-in.
    // Over in MicrosoftAuthRedirect.tsx we then use the redirectUrl query param to redirect to the
    // oauthUrl that we want to use.
    window.Office.context.ui.displayDialogAsync(
      getSiteUrl() +
        outlookPaths.authRedirect +
        "?redirectUrl=" +
        encodeURIComponent(oauthUrl) +
        `&${m365NonceUrlParam}=${m365None}`, // We have to pass the nonce to the redirect page because Safari and Firefox don't allow modal to access localStorage.
      {
        width: 35, // percentage of screen width
        height: 70, // percentage of screen height
      },
      (dialog) => {
        if (dialog.status === window.Office.AsyncResultStatus.Succeeded) {
          const createdDialog = dialog.value;

          const handler = (event: { message: string; origin: string | undefined }) => {
            const parsed = JSON.parse(event.message) as Record<string, string>; // Cast to any to avoid TS error as we know the shape of the object
            const success = parsed.success,
              authCode = parsed.authCode,
              error = parsed.error;

            if (error || !success || !authCode) {
              const errorMessage = error || "Invalid auth response";
              setError(errorMessage);
              onFailure?.({ error: errorMessage });
            } else {
              const response = { code: authCode };
              setData(response);
              setError("");
              onSuccess?.(response);
            }
            setLoading(false);
          };

          createdDialog.addEventHandler(window.Office.EventType.DialogMessageReceived, handler);
          createdDialog.addEventHandler(window.Office.EventType.DialogEventReceived, () => {
            setLoading(false); // Dialog closed or error happened.
            const errorMsg = "User closed Outlook Add-in auth modal";
            setError(errorMsg);
            onFailure?.({ error: errorMsg });
          });
        } else {
          setLoading(false);
          const errorMsg = "Failed to open Outlook Add-in auth modal user denied access to modal";
          setError(errorMsg);
          onFailure?.({ error: errorMsg });
        }
      },
    );
  }, []);

  return {
    prompt,
    loading,
    error,
    data,
  } as const;
};
